import BoxResizeWrapper from "@components/Atoms/BoxResizeWrapper";
import type { IconCode } from "@components/Atoms/Icon/LucideIcon";
import ActionButton from "@components/controls/HoverController/ActionButton";
import { cn } from "@core-ui/utils/cn";
import styled from "@emotion/styled";
import t from "@ext/localization/locale/translate";
import { SortState } from "@ext/markdown/elements/table/edit/model/tableTypes";
import { CheckboxField, type CheckedState } from "@ui-kit/Checkbox";
import { Command, CommandItem, CommandList } from "@ui-kit/Command";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ui-kit/Dropdown";
import { Icon } from "@ui-kit/Icon";
import { SearchSelectInput } from "@ui-kit/SearchSelect";
import { ToggleGroup, ToggleGroupItem } from "@ui-kit/ToggleGroup";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui-kit/Tooltip";
import { useCallback, useMemo, useState } from "react";

interface FilterButtonProps {
	columnValues: string[];
	filteredValues: string[];
	handleFilterChange: (excluded: string[]) => void;
	handleSortChange: (sortState: SortState) => void;
	sort: SortState;
	canSort: boolean;
}

const Icons: Record<SortState, IconCode> = {
	asc: "arrow-up-narrow-wide",
	desc: "arrow-down-wide-narrow",
};

const ButtonContainer = styled.div`
	position: absolute;
	right: 0;
	bottom: 0;
	z-index: 1;

	opacity: 0;
	transition: opacity var(--transition-time-fast) ease-in-out;

	&.isOpen,
	&.filtered {
		opacity: 1;
	}

	:hover {
		opacity: 1;
	}
`;

const StyledDropdownMenuContent = styled(DropdownMenuContent)`
	.content-container {
		height: 100%;
		display: grid;
		grid-template-rows: auto auto auto auto minmax(10rem, 1fr);
	}

	.toggle-group {
		flex-direction: column;
		gap: 0;

		[data-state="off"] > svg {
			display: none;
		}

		[data-state="on"]{
			background-color: unset;
		}

		> button {
			width: 100%;
			justify-content: space-between;
			padding: 0.5rem;
			height: 2rem;

			.toggle-content {
				display: flex;
				gap: 0.5rem;
			}
		}
	}

	.filter-search {
		max-height: unset;
		padding: 0.25rem;

		.check-box-field {
			> * {
				max-width: calc(100% - 12px - 16px);
			}
			div:has(>label) {
				height: auto;

				label {
					line-height: 1.25rem;
					text-overflow: ellipsis;
					white-space: nowrap;
					display: inline-block;
					overflow: hidden;
					height: auto;
					font-weight: inherit;
				}
			}

			&.empty-label label {
				color: hsl(var(--muted));
			}
		}
	}
`;

const FilterAndSortButton = (props: FilterButtonProps) => {
	const { columnValues = [], filteredValues = [], handleFilterChange, handleSortChange, sort, canSort } = props;

	const [height, setHeight] = useState(478);
	const [width, setWidth] = useState(267);

	const onResizeEnd = useCallback((event: MouseEvent) => {
		const wrapper = (event.target as HTMLDivElement)?.parentElement?.parentElement;
		if (!wrapper) return;

		const width = wrapper.clientWidth;
		const height = wrapper.clientHeight;

		setWidth(width);
		setHeight(height);
	}, []);

	const [isOpen, setIsOpen] = useState(false);
	const filtered = filteredValues.length || sort;
	const icon = filtered ? "funnel" : "funnel-plus";

	const checkedState: CheckedState = useMemo(() => {
		if (filteredValues.length === 0) return true;
		if (filteredValues.length === columnValues.length) return false;
		return "indeterminate";
	}, [columnValues, filteredValues]);

	const onCheckedChange = useCallback(
		(value: string) => {
			const next = filteredValues.includes(value)
				? filteredValues.filter((v) => v !== value)
				: [...filteredValues, value];
			handleFilterChange(next);
		},
		[filteredValues, handleFilterChange],
	);

	const onCheckAll = useCallback(() => {
		const next = filteredValues.length === 0 ? [...columnValues] : [];
		handleFilterChange(next);
	}, [filteredValues, handleFilterChange, columnValues]);

	const toggleGroup = (
		<ToggleGroup
			className="toggle-group"
			defaultValue={sort}
			disabled={!canSort}
			onValueChange={handleSortChange}
			type="single"
		>
			{Object.values(SortState).map((v: SortState) => (
				<ToggleGroupItem key={v} value={v}>
					<div className={cn("toggle-content", "font-normal")}>
						<Icon icon={Icons[v]}></Icon>
						{t(`editor.table.sort.${v}`)}
					</div>
					<Icon icon="check"></Icon>
				</ToggleGroupItem>
			))}
		</ToggleGroup>
	);

	const labelClassName = cn("text-xs", "font-normal", "text-muted");

	return (
		<DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
			<DropdownMenuTrigger asChild>
				<ButtonContainer
					className={cn("button-container", filtered ? "filtered" : null, isOpen ? "isOpen" : null)}
				>
					<ActionButton icon={icon} tooltipText={""} />
				</ButtonContainer>
			</DropdownMenuTrigger>
			<StyledDropdownMenuContent align="start">
				<BoxResizeWrapper
					maxHeight={window.innerHeight * 0.8}
					maxWidth={window.innerWidth * 0.5}
					minHeight={window.innerHeight * 0.06}
					minWidth={window.innerWidth * 0.1}
					onResizeEnd={onResizeEnd}
					style={{ height, width }}
				>
					<div className="content-container">
						<DropdownMenuLabel className={labelClassName}>
							{t("properties.view.order-by")}
						</DropdownMenuLabel>
						{canSort ? (
							toggleGroup
						) : (
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>{toggleGroup}</TooltipTrigger>
									<TooltipContent>
										<p className="max-w-xs">{t("editor.table.sort.not-available")}</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						)}
						<DropdownMenuSeparator />
						<DropdownMenuLabel className={labelClassName}>{t("properties.view.filter")}</DropdownMenuLabel>
						<Command>
							<SearchSelectInput placeholder={t("find2")} />
							<CommandList className="filter-search" key={`list-${"searchQuery"}`}>
								<CommandItem key={"*"} value={"*"}>
									<CheckboxField
										checked={checkedState}
										className={cn("w-full", "check-box-field", "font-normal")}
										label={t("properties.select-all")}
										onCheckedChange={onCheckAll}
									/>
								</CommandItem>
								{columnValues.map((value) => (
									<CommandItem key={`v-${value}`} title={value} value={value}>
										<CheckboxField
											checked={!filteredValues.includes(value)}
											className={cn(
												"w-full",
												"check-box-field",
												"font-normal",
												value ? "" : "empty-label",
											)}
											label={value || t("properties.empty")}
											onCheckedChange={() => onCheckedChange(value)}
										/>
									</CommandItem>
								))}
							</CommandList>
						</Command>
					</div>
				</BoxResizeWrapper>
			</StyledDropdownMenuContent>
		</DropdownMenu>
	);
};

export default FilterAndSortButton;
